import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { api } from '../lib/api';

const RISK_COLOR: any = ['match', ['get', 'risk_class'],
  'high', '#dc2626', 'medium', '#f59e0b', 'low', '#16a34a', '#16a34a'];
const TREE_COLOR: any = ['interpolate', ['linear'], ['get', 'height'], 8, '#86efac', 15, '#22c55e', 25, '#14532d'];

const btn: React.CSSProperties = {
  background: '#1e293b', color: '#fff', border: 'none', borderRadius: 6,
  padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

// Build small square polygons from tree points so they can be extruded in 3D.
function treeSquares(fc: any, limit: number) {
  const feats = (fc?.features || []).slice(0, limit).map((f: any) => {
    const c = f.geometry?.coordinates; if (!c) return null;
    const lon = c[0], lat = c[1], h = Number(f.properties?.canopy_m) || 10;
    const m = 4, dLat = m / 111320, dLon = m / (111320 * Math.cos(lat * Math.PI / 180));
    return {
      type: 'Feature', properties: { height: h },
      geometry: { type: 'Polygon', coordinates: [[[lon - dLon, lat - dLat], [lon + dLon, lat - dLat], [lon + dLon, lat + dLat], [lon - dLon, lat + dLat], [lon - dLon, lat - dLat]]] },
    };
  }).filter(Boolean);
  return { type: 'FeatureCollection', features: feats };
}

export default function MakeReadyMap({ projectId, onSelectSegment }: { projectId: string; onSelectSegment: (id: string) => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [is3d, setIs3d] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: {
        version: 8,
        sources: {
          osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap' },
          dem: { type: 'raster-dem', tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'], encoding: 'terrarium', tileSize: 256, maxzoom: 14 },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      },
      center: [-92.3, 33.8], zoom: 10, pitch: 0, bearing: 0, antialias: true,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');

    map.on('load', async () => {
      try { map.addLayer({ id: 'hillshade', type: 'hillshade', source: 'dem', paint: { 'hillshade-exaggeration': 0.5 } as any }); } catch { /* dem may fail */ }

      const [fc, trees, buildings, svpoles] = await Promise.all([
        api.get(`/bead/projects/${projectId}/makeready/geojson`),
        api.get(`/bead/projects/${projectId}/makeready/trees`),
        api.get(`/bead/projects/${projectId}/buildings`),
        api.get(`/bead/projects/${projectId}/sv-poles/geojson`),
      ]);

      map.addSource('buildings', { type: 'geojson', data: buildings });
      map.addLayer({
        id: 'buildings-3d', type: 'fill-extrusion', source: 'buildings',
        paint: { 'fill-extrusion-color': '#9aa7b4', 'fill-extrusion-height': ['coalesce', ['get', 'height'], 6], 'fill-extrusion-base': 0, 'fill-extrusion-opacity': 0.85 },
      });

      // trees: flat dots for 2D, extruded columns for 3D
      map.addSource('trees', { type: 'geojson', data: trees });
      map.addLayer({
        id: 'trees', type: 'circle', source: 'trees',
        paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 16, 5], 'circle-color': ['interpolate', ['linear'], ['get', 'canopy_m'], 8, '#86efac', 15, '#22c55e', 25, '#14532d'], 'circle-opacity': 0.6 },
      });
      map.addSource('trees-poly', { type: 'geojson', data: treeSquares(trees, 2500) });
      map.addLayer({
        id: 'trees-3d', type: 'fill-extrusion', source: 'trees-poly', layout: { visibility: 'none' },
        paint: { 'fill-extrusion-color': TREE_COLOR, 'fill-extrusion-height': ['get', 'height'], 'fill-extrusion-base': 0, 'fill-extrusion-opacity': 0.9 },
      });

      map.addSource('route', { type: 'geojson', data: fc });
      map.addLayer({
        id: 'route-glow', type: 'line', source: 'route', filter: ['==', ['get', 'risk_class'], 'high'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#dc2626', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 5, 16, 14], 'line-blur': 3, 'line-opacity': 0.45 },
      });
      map.addLayer({
        id: 'route', type: 'line', source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': RISK_COLOR, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 16, 7] },
      });
      // wide transparent layer so clicking anywhere near the line works
      map.addLayer({ id: 'route-hit', type: 'line', source: 'route', paint: { 'line-color': '#000', 'line-opacity': 0, 'line-width': 18 } });

      map.addSource('svpoles', { type: 'geojson', data: svpoles });
      map.addLayer({ id: 'svpoles', type: 'circle', source: 'svpoles',
        paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 16, 7],
          'circle-color': ['match', ['get', 'worst'], 'flag', '#dc2626', '#16a34a'],
          'circle-stroke-width': 1.2, 'circle-stroke-color': '#fff' } });
      map.on('click', 'svpoles', (e) => { const f = e.features?.[0] as any; if (f) window.open(`https://www.google.com/maps/@?api=1&map_action=pano&pano=${f.properties.pano_id}&heading=${f.properties.heading}`, '_blank'); });
      map.on('mouseenter', 'svpoles', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'svpoles', () => { map.getCanvas().style.cursor = ''; });

      try {
        const b = new maplibregl.LngLatBounds();
        for (const f of (fc.features || [])) {
          const g = f.geometry; if (!g) continue;
          const cs = g.type === 'MultiLineString' ? g.coordinates.flat() : g.coordinates;
          for (const c of cs) b.extend(c as any);
        }
        if (!b.isEmpty()) map.fitBounds(b, { padding: 50, duration: 0 });
      } catch { /* ignore */ }

      const pick = (e: any) => { const f = e.features?.[0]; const sid = f?.properties?.segment_id; if (sid) onSelectSegment(String(sid)); };
      map.on('click', 'route-hit', pick);
      map.on('mouseenter', 'route-hit', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'route-hit', () => { map.getCanvas().style.cursor = ''; });
      setReady(true);
    });

    return () => { map.remove(); mapRef.current = null; };
  }, [projectId]);

  const toggle3d = () => {
    const map = mapRef.current; if (!map) return;
    const next = !is3d; setIs3d(next);
    const vis = (id: string, v: 'visible' | 'none') => { try { map.setLayoutProperty(id, 'visibility', v); } catch { /* */ } };
    if (next) {
      try { map.setTerrain({ source: 'dem', exaggeration: 2.0 }); } catch { /* */ }
      vis('trees-3d', 'visible'); vis('trees', 'none');
      map.easeTo({ pitch: 65, duration: 900 });
    } else {
      try { map.setTerrain(null as any); } catch { /* */ }
      vis('trees-3d', 'none'); vis('trees', 'visible');
      map.easeTo({ pitch: 0, bearing: 0, duration: 900 });
    }
  };
  const orbit = () => { const map = mapRef.current; if (!map) return; map.easeTo({ bearing: map.getBearing() + 150, duration: 8000, easing: (t) => t }); };

  return (
    <div style={{ position: 'relative', width: '100%', height: 500, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', marginBottom: 18 }}>
      <div ref={ref} style={{ position: 'absolute', inset: 0 }} />
      <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, zIndex: 2 }}>
        <button onClick={toggle3d} style={{ ...btn, background: is3d ? '#4338ca' : '#1e293b' }}>{is3d ? '3D ✓' : '2D / 3D'}</button>
        <button onClick={orbit} style={btn} disabled={!ready}>Orbit</button>
      </div>
      <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(255,255,255,0.92)', borderRadius: 6, padding: '6px 10px', fontSize: 11, zIndex: 2, lineHeight: 1.6 }}>
        <b>Make-ready risk</b><br />
        <span style={{ color: '#dc2626' }}>▬</span> high&nbsp; <span style={{ color: '#f59e0b' }}>▬</span> medium&nbsp; <span style={{ color: '#16a34a' }}>▬</span> low&nbsp; · &nbsp;<span style={{ color: '#15803d' }}>●</span> tall canopy&nbsp; · &nbsp;<span style={{ color: '#9aa7b4' }}>▮</span> buildings&nbsp; · &nbsp;<span style={{ color: '#dc2626' }}>◉</span> poles
        <br /><span style={{ color: '#64748b' }}>Click any line for its profile. 3D + Orbit for the flyover (relief is subtle on flat terrain).</span>
      </div>
    </div>
  );
}
